import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { healthApi } from './services/api';
import { wsService } from './services/websocket';
import { useStore } from './store/useStore';
import { AuthProvider } from './providers/AuthProvider';
import { useAuth } from './hooks/useAuth';
import AuthGuard from './components/auth/AuthGuard';
import DashboardOverview from './components/DashboardOverview';
import TenantsManager from './components/TenantsManager';
import CertificatesManager from './components/CertificatesManager';
import DocumentsManager from './components/DocumentsManager';
import SystemMonitor from './components/SystemMonitor';
import NotificationCenter from './components/NotificationCenter';
import UsersManager from './components/UsersManager';
import TenantDashboard from './components/tenant/TenantDashboard';
import MyDocuments from './components/tenant/MyDocuments';
import MyCertificates from './components/tenant/MyCertificates';
import TenantSettings from './components/tenant/TenantSettings';
import { toast } from 'react-hot-toast';
import './App.css';

const AdminApp = () => {
  const {
    activeTab,
    systemHealth,
    isWebSocketConnected,
    user,
    setActiveTab,
    setSystemHealth,
    setWebSocketConnected,
    getHealthyServicesCount,
    getUnreadNotifications,
    logout,
  } = useStore();

  const [showNotifications, setShowNotifications] = React.useState(false);
  const unreadCount = getUnreadNotifications().length;

  useEffect(() => {
    // Initialize WebSocket connection (disabled temporarily)
    // wsService.connect();

    // Check WebSocket status (disabled temporarily)
    const checkWebSocketStatus = () => {
      // setWebSocketConnected(wsService.isConnected());
      setWebSocketConnected(false); // Set to false since WebSocket is disabled
    };

    // Check system health on component mount
    checkSystemHealth();

    // Set up health check interval
    const healthInterval = setInterval(checkSystemHealth, 60000); // Every 60 seconds (reduced from 30)
    const wsStatusInterval = setInterval(checkWebSocketStatus, 10000); // Every 10 seconds (reduced from 5)

    // Listen for WebSocket events
    const handleMetricsUpdate = (event: CustomEvent) => {
      // Handle real-time metrics update
      console.log('Metrics updated:', event.detail);
    };

    const handleNotification = (event: CustomEvent) => {
      // Handle real-time notification
      console.log('New notification:', event.detail);
    };

    window.addEventListener('metrics-update', handleMetricsUpdate as EventListener);
    window.addEventListener('notification', handleNotification as EventListener);

    return () => {
      clearInterval(healthInterval);
      clearInterval(wsStatusInterval);
      window.removeEventListener('metrics-update', handleMetricsUpdate as EventListener);
      window.removeEventListener('notification', handleNotification as EventListener);
      // wsService.disconnect(); // Disabled temporarily
    };
  }, []);

  const checkSystemHealth = async () => {
    try {
      // Only check essential services (analytics and notifications are optional)
      const [tenants, certificates, documents, auth] = await Promise.allSettled([
        healthApi.tenants(),
        healthApi.certificates(),
        healthApi.documents(),
        healthApi.auth(),
      ]);

      setSystemHealth({
        tenants: tenants.status === 'fulfilled',
        analytics: false, // Analytics service not running (optional)
        notifications: false, // Notifications service not running (optional)
        certificates: certificates.status === 'fulfilled',
        documents: documents.status === 'fulfilled',
        auth: auth.status === 'fulfilled',
        sunat: documents.status === 'fulfilled', // Using documents as proxy for sunat
      });
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'tenants', name: 'Tenants', icon: '🏢' },
    { id: 'users', name: 'Usuarios', icon: '👥' },
    { id: 'certificates', name: 'Certificates', icon: '🔐' },
    { id: 'documents', name: 'Documents', icon: '📄' },
    { id: 'monitor', name: 'System Monitor', icon: '📈' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardOverview />;
      case 'tenants':
        return <TenantsManager />;
      case 'users':
        return <UsersManager />;
      case 'certificates':
        return <CertificatesManager />;
      case 'documents':
        return <DocumentsManager />;
      case 'monitor':
        return <SystemMonitor />;
      case 'notifications':
        return <NotificationsPanel />;
      default:
        return <DashboardOverview />;
    }
  };

  const NotificationsPanel = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Notifications & Alerts</h2>
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">Notifications panel - Coming soon with real-time updates!</p>
      </div>
    </div>
  );

  const getHealthColor = (isHealthy: boolean) =>
    isHealthy ? 'text-green-500' : 'text-red-500';

  const handleLogout = () => {
    try {
      logout();
      toast.success('Sesión cerrada exitosamente');
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <img src="/logo192.png?v=3" alt="SOR" className="w-9 h-9 rounded-lg mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">
              SOR <span className="font-normal text-gray-500">Admin</span>
            </h1>
            <span className="ml-3 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              v5.0.0
            </span>
          </div>

            {/* System Health Status */}
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  <span className="text-gray-600">Services:</span>
                  <span className={`font-bold ${getHealthyServicesCount() === 7 ? 'text-green-600' : 'text-orange-600'}`}>
                    {getHealthyServicesCount()}/7
                  </span>
                </div>

                <div className="flex items-center space-x-1">
                  <span
                    className={`w-2 h-2 rounded-full ${isWebSocketConnected ? 'bg-green-500' : 'bg-red-500'}`}
                    title={isWebSocketConnected ? 'Live updates connected' : 'Live updates disconnected'}
                  />
                  <span className="text-gray-600 text-xs">
                    {isWebSocketConnected ? 'Live' : 'Offline'}
                  </span>
                </div>

                <div className="hidden md:flex items-center space-x-2">
                  <span className={`text-xs font-medium ${getHealthColor(systemHealth.analytics)}`}>
                    Analytics {systemHealth.analytics ? '✓' : '✗'}
                  </span>
                  <span className={`text-xs font-medium ${getHealthColor(systemHealth.notifications)}`}>
                    Notifications {systemHealth.notifications ? '✓' : '✗'}
                  </span>
                  <span className={`text-xs font-medium ${getHealthColor(systemHealth.tenants)}`}>
                    Platform {systemHealth.tenants ? '✓' : '✗'}
                  </span>
                </div>

                {/* Notification Button */}
                <button
                  onClick={() => setShowNotifications(true)}
                  className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <span className="text-xl">🔔</span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* User Info & Logout */}
                <div className="flex items-center space-x-3 border-l border-gray-200 pl-4">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">
                      {user?.username || 'Usuario'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {user?.role || 'N/A'}
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    title="Cerrar sesión"
                  >
                    <span className="mr-1">🚪</span>
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-8">
          {/* Sidebar Navigation */}
          <nav className="w-64 space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="text-xl mr-3">{tab.icon}</span>
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </nav>

          {/* Main Content */}
          <main className="flex-1">
            {renderTabContent()}
          </main>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        }}
      />

      {/* Notification Center */}
      <NotificationCenter
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
};

const TenantApp = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = React.useState('dashboard');

  const tenantTabs = [
    { id: 'dashboard', name: 'Panel Principal', icon: '📊', component: <TenantDashboard /> },
    { id: 'documents', name: 'Mis Documentos', icon: '📄', component: <MyDocuments /> },
    { id: 'certificates', name: 'Mis Certificados', icon: '🔐', component: <MyCertificates /> },
    { id: 'settings', name: 'Configuración', icon: '⚙️', component: <TenantSettings /> },
  ];

  const renderTabContent = () => {
    const tab = tenantTabs.find(t => t.id === activeTab);
    return tab ? tab.component : <TenantDashboard />;
  };

  const handleLogout = () => {
    try {
      // TODO: Implement logout logic with auth context
      toast.success('Sesión cerrada exitosamente');
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Tenant Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                Portal del Contribuyente
              </h1>
              <span className="ml-3 px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                Tenant
              </span>
            </div>

            {/* User Info & Logout */}
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">
                  {user?.username || 'Usuario'}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.role || 'tenant_user'}
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                title="Cerrar sesión"
              >
                <span className="mr-1">🚪</span>
                Salir
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-8">
          {/* Tenant Sidebar Navigation */}
          <nav className="w-64 space-y-2">
            {tenantTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-4 py-3 text-left rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="text-xl mr-3">{tab.icon}</span>
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </nav>

          {/* Main Content */}
          <main className="flex-1">
            {renderTabContent()}
          </main>
        </div>
      </div>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: '0.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        }}
      />
    </div>
  );
};

const AppContent = () => {
  const { user } = useAuth();

  if (!user) {
    return <div>Loading...</div>;
  }

  // Role-based routing
  if (user.role === 'tenant_user') {
    return <TenantApp />;
  }

  // Admin/SuperAdmin interface
  return <AdminApp />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AuthGuard>
          <AppContent />
        </AuthGuard>
      </Router>
    </AuthProvider>
  );
}

export default App;
